import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { AlertTriangle, RefreshCw, X, ChevronDown, ChevronUp } from "lucide-react";

/** Format an ISO timestamp into a compact "12 May, 14:32" label. */
function fmt(ts) {
  if (!ts) return "—";
  try {
    return new Date(ts).toLocaleString("en-NZ", {
      day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return ts;
  }
}

/** Sticky panel showing every shift the auto-sync has given up on, with
 *  retry / dismiss / bulk-retry actions. Designed to live inside the
 *  /admin/xero page. */
export function XeroDeadLetterPanel({ canSync = true }) {
  const [data, setData] = useState({ dead_letter: [], max_retries: 5, failed_still_retrying: 0, dismissed_count: 0 });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [dismissTarget, setDismissTarget] = useState(null);
  const [dismissReason, setDismissReason] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: payload } = await api.get("/admin/xero/dead-letter");
      setData(payload);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load dead-letter inbox");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const retryOne = async (sid) => {
    setBusyId(sid);
    try {
      await api.post(`/admin/xero/dead-letter/${sid}/retry`);
      toast.success("Reset — will be retried on the next sync.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry failed");
    } finally {
      setBusyId(null);
    }
  };

  const openDismiss = (row) => {
    setDismissTarget(row);
    setDismissReason("");
  };

  const submitDismiss = async () => {
    if (!dismissTarget) return;
    if (!dismissReason.trim()) {
      toast.error("Please write a short reason — it goes into the audit trail.");
      return;
    }
    setBusyId(dismissTarget.shift_id);
    try {
      await api.post(`/admin/xero/dead-letter/${dismissTarget.shift_id}/dismiss`, {
        reason: dismissReason.trim(),
      });
      toast.success("Dismissed — this shift will no longer be retried.");
      setDismissTarget(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dismiss failed");
    } finally {
      setBusyId(null);
    }
  };

  const retryAll = async () => {
    setBulkBusy(true);
    try {
      const { data: res } = await api.post("/admin/xero/dead-letter/retry-all");
      toast.success(`Reset ${res.retried} stuck shift${res.retried === 1 ? "" : "s"}. Click Sync Now when ready.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk retry failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const total = data.dead_letter.length;
  const stillRetrying = data.failed_still_retrying;
  const dismissedCount = data.dismissed_count;

  // If everything's clean, render a compact "all clear" tile.
  if (!loading && total === 0 && stillRetrying === 0) {
    return (
      <Card className="border-emerald-100 bg-emerald-50/40">
        <CardContent className="p-5 flex items-center gap-3" data-testid="xero-dlq-clean">
          <div className="h-10 w-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
            <RefreshCw className="h-5 w-5 text-emerald-600" />
          </div>
          <div className="flex-1">
            <div className="font-semibold text-[#1A2744] text-sm">No stuck timesheets</div>
            <div className="text-[12px] text-emerald-700">
              Every approved shift either synced cleanly or was acknowledged.
              {dismissedCount > 0 ? ` ${dismissedCount} dismissed historically.` : ""}
            </div>
          </div>
          <Button size="sm" variant="ghost" onClick={load} className="text-[11px]" data-testid="xero-dlq-refresh">
            Refresh
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-red-200" data-testid="xero-dlq-panel">
      <CardContent className="p-0">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="w-full flex items-center justify-between px-6 py-4 hover:bg-red-50/40 transition-colors text-left"
          data-testid="xero-dlq-toggle"
        >
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-red-500/10 flex items-center justify-center shrink-0">
              <AlertTriangle className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <div className="font-heading text-base font-bold text-[#1A2744]">
                Stuck timesheet sync
                {total > 0 && (
                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-[11px] font-bold" data-testid="xero-dlq-count">
                    {total}
                  </span>
                )}
              </div>
              <div className="text-[12px] text-gray-600 mt-0.5">
                {total > 0
                  ? `${total} shift${total === 1 ? "" : "s"} dead-lettered after ${data.max_retries} retries.`
                  : "No dead-lettered shifts right now."}
                {stillRetrying > 0 && (
                  <span className="ml-1.5 text-amber-700">
                    · {stillRetrying} still retrying
                  </span>
                )}
              </div>
            </div>
          </div>
          {expanded ? <ChevronUp className="h-5 w-5 text-gray-500" /> : <ChevronDown className="h-5 w-5 text-gray-500" />}
        </button>

        {expanded && (
          <>
            <div className="px-6 pb-4 flex items-center gap-2 border-t border-red-100 pt-4">
              <Button
                size="sm"
                variant="outline"
                onClick={retryAll}
                disabled={bulkBusy || total === 0 || !canSync}
                className="border-red-300 text-red-700 hover:bg-red-50"
                data-testid="xero-dlq-retry-all"
              >
                <RefreshCw className={`h-4 w-4 mr-1 ${bulkBusy ? "animate-spin" : ""}`} />
                Retry all {total > 0 ? `(${total})` : ""}
              </Button>
              <Button size="sm" variant="ghost" onClick={load} disabled={loading} className="text-[11px]" data-testid="xero-dlq-refresh-btn">
                <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
              </Button>
              <div className="ml-auto text-[11px] text-gray-500">
                Auto-sync skips dead-lettered + dismissed rows. Fix the cause, then click Retry.
              </div>
            </div>

            {total === 0 ? (
              <div className="px-6 pb-6 text-sm text-gray-500" data-testid="xero-dlq-empty-state">
                Nothing dead-lettered. Open the sync history above to see what's still being retried.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead>Shift date</TableHead>
                      <TableHead>Hours</TableHead>
                      <TableHead>Attempts</TableHead>
                      <TableHead>Last error</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.dead_letter.map((row) => (
                      <TableRow key={row.shift_id} data-testid={`xero-dlq-row-${row.shift_id}`}>
                        <TableCell>
                          <div className="font-medium text-[#1A2744]">{row.employee_name}</div>
                          <div className="text-[11px] text-gray-500 font-mono">
                            {row.employee_code || "—"}
                            {!row.linked_to_xero && (
                              <span className="ml-1.5 text-[10px] uppercase tracking-wide text-amber-700">Unlinked</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-[12px]">{fmt(row.clock_in_ts)}</TableCell>
                        <TableCell className="text-[12px]">
                          {row.total_minutes != null
                            ? `${(row.total_minutes / 60).toFixed(2)}h`
                            : "—"}
                        </TableCell>
                        <TableCell>
                          <div className="text-[12px]">{row.xero_attempts} / {data.max_retries}</div>
                          <div className="text-[10px] text-gray-500">since {fmt(row.xero_first_failed_at)}</div>
                        </TableCell>
                        <TableCell className="max-w-md">
                          <div className="text-[11px] text-red-700 line-clamp-2 font-mono break-all" title={row.xero_error}>
                            {row.xero_error || "—"}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center gap-1.5 justify-end">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => retryOne(row.shift_id)}
                              disabled={busyId === row.shift_id || !canSync}
                              className="h-7 text-[11px]"
                              data-testid={`xero-dlq-retry-${row.shift_id}`}
                            >
                              <RefreshCw className={`h-3 w-3 mr-1 ${busyId === row.shift_id ? "animate-spin" : ""}`} />
                              Retry
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openDismiss(row)}
                              disabled={busyId === row.shift_id}
                              className="h-7 text-[11px] text-gray-500 hover:text-red-700"
                              data-testid={`xero-dlq-dismiss-${row.shift_id}`}
                            >
                              <X className="h-3 w-3 mr-1" />
                              Dismiss
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </>
        )}
      </CardContent>

      <Dialog open={!!dismissTarget} onOpenChange={(o) => !o && setDismissTarget(null)}>
        <DialogContent className="max-w-md" data-testid="xero-dlq-dismiss-dialog">
          <DialogHeader>
            <DialogTitle>Dismiss this stuck shift</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-gray-600">
              The auto-sync will <strong>never</strong> attempt this shift again. Use this when the shift was already recorded directly in Xero, or shouldn't have been billable.
            </p>
            {dismissTarget && (
              <div className="rounded-lg bg-gray-50 border border-gray-200 p-3 text-[12px]">
                <div className="font-semibold text-[#1A2744]">{dismissTarget.employee_name} <span className="font-mono text-gray-500">({dismissTarget.employee_code || "—"})</span></div>
                <div className="text-gray-600 mt-0.5">{fmt(dismissTarget.clock_in_ts)} · {dismissTarget.total_minutes ? `${(dismissTarget.total_minutes / 60).toFixed(2)}h` : "—"}</div>
              </div>
            )}
            <div>
              <label className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">Reason (audit trail)</label>
              <Textarea
                rows={3}
                value={dismissReason}
                onChange={(e) => setDismissReason(e.target.value)}
                placeholder="e.g. Recorded directly in Xero by hand for the Apr 14 manual run."
                className="mt-1"
                data-testid="xero-dlq-dismiss-reason"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDismissTarget(null)} data-testid="xero-dlq-dismiss-cancel">Cancel</Button>
            <Button
              onClick={submitDismiss}
              disabled={!dismissReason.trim() || busyId === dismissTarget?.shift_id}
              className="bg-red-600 hover:bg-red-700"
              data-testid="xero-dlq-dismiss-confirm"
            >
              Dismiss shift
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
